/* Global styles FIRST. Vite emits CSS in module-graph order, so importing this last put
   .card AFTER every component stylesheet. Both are single-class selectors, so .card won every
   tie and silently repainted anything that tried to give a card its own background: the Today
   strip rendered as a white box with white text in it, and the same trap was set for every
   other `card`-plus-modifier in the app. Base styles belong first. */
import './styles/global.css';

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { Shell } from './app/Shell';
import { SignIn } from './app/SignIn';
import { AuthProvider, useAuth } from './lib/auth';
import { TENANT } from '@tenant';

/* Tenant brand, normally loaded from app.org_brand. Applied as CSS custom properties so
   that re-skinning for a white-label tenant is a data change, never a code change. */
const TENANT_BRAND = TENANT.brand;

/* index.html carries a static title, which is wrong for a white-label product: every tenant
   would share one browser tab name. Set from the tenant instead. */
document.title = TENANT.brand.productName;

function applyBrand(b: typeof TENANT_BRAND) {
  const r = document.documentElement.style;
  r.setProperty('--brand-ink', b.ink);
  r.setProperty('--brand-accent', b.accent);
  r.setProperty('--brand-paper', b.paper);
  r.setProperty('--brand-ink-dark', b.inkDark);
  r.setProperty('--brand-paper-dark', b.paperDark);
}
applyBrand(TENANT_BRAND);

const stored = localStorage.getItem('forum.theme');
if (stored) document.documentElement.setAttribute('data-theme', stored);

export const Brand = TENANT_BRAND;

/* The gate. Three states, in order of how often they occur:

   ready + session   -> the application
   ready + none      -> sign in
   not ready         -> nothing at all, briefly

   That third state matters more than it looks. Rendering the sign-in screen while the
   session lookup is still in flight would flash the login form at someone who is already
   signed in, on every single page load. */
function Gate() {
  const { ready, demo, session } = useAuth();

  if (!ready) {
    return <div className="boot" aria-busy="true" aria-label="Loading" />;
  }
  // No backend means seed data with nothing real in it, so a credential prompt would be
  // theatre. See the reasoning in lib/auth.tsx.
  if (!demo && !session) {
    return <SignIn productName={TENANT_BRAND.productName} orgName={TENANT_BRAND.orgName} />;
  }
  return <Shell brand={TENANT_BRAND} />;
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter>
      <AuthProvider>
        <Gate />
      </AuthProvider>
    </BrowserRouter>
  </React.StrictMode>,
);
